import moment from "moment-timezone";
import "moment-precise-range-plugin";
export const DATE_TIME_FORMATS = {
  default: "YYYY-MM-DD HH:mm:ss",
  // default format when displaying date times
  complete: "YYYY-MM-DD HH:mm:ss.SSS",
  // includes ms, useful were precise time is important
  withTz: "YYYY-MM-DD HH:mm:ss Z",
  // includes the time zone
  readable: "dddd D MMMM YYYY, HH:mm ZZ",
  // easy to read
  shortReadable: "MMM D, YYYY",
  // easy to read
  internal: "YYYY-MM-DDTHH:mm:ss.SSSZ",
  // ISO 8601, internal default, not really nice to read. Mostly used communication with the API.
  internalIndexer: "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
  // ISO 8601, used for ES search queries, when a timestamp has to be reformatted
  date: "YYYY-MM-DD",
  hourAndMinute: "HH:mm",
  dateHourAndMinute: "YYYY-MM-DD HH:mm"
};
const DEFAULT_OUTPUT_TZ = "UTC";
const validateDateTime = (dateTime, originalDateTime, additionalInfo) => {
  if (!dateTime.isValid()) {
    let errorMessage = `Date time ${originalDateTime} is not valid.`;
    if (additionalInfo) {
      errorMessage = `${errorMessage} ${additionalInfo}`;
    }
    console.error(errorMessage);
  }
  return dateTime;
};
const getFormatStringsForDateTimeFormats = (dateTimeFormats) => dateTimeFormats == null ? void 0 : dateTimeFormats.map((dateTimeFormat) => {
  const format = DATE_TIME_FORMATS[dateTimeFormat];
  if (!format) {
    throw new Error(`Provided date time format "${dateTimeFormat}" is not supported.`);
  }
  return format;
});
export const toDateObject = (dateTime, acceptedFormats, tz = DEFAULT_OUTPUT_TZ) => {
  const acceptedFormatStrings = getFormatStringsForDateTimeFormats(acceptedFormats);
  const dateObject = moment.utc(dateTime, acceptedFormatStrings, true).tz(tz);
  const validationInfo = (acceptedFormats == null ? void 0 : acceptedFormats.length) ? `Expected formats: ${acceptedFormatStrings.join(", ")}.` : void 0;
  return validateDateTime(dateObject, dateTime, validationInfo);
};
export const parseFromIsoString = (dateTimeString, tz = DEFAULT_OUTPUT_TZ) => toDateObject(dateTimeString, ["internal"], tz);
export const getBrowserTimezone = () => moment.tz.guess();
export const adjustFormat = (dateTime, format = "default", tz = DEFAULT_OUTPUT_TZ) => toDateObject(dateTime, void 0, tz).format(DATE_TIME_FORMATS[format]);
export const formatAsBrowserTime = (time, format = "default") => adjustFormat(time, format, getBrowserTimezone());
export const relativeDifference = (dateTime, withoutSuffix = false) => {
  const dateObject = toDateObject(dateTime);
  return validateDateTime(dateObject, dateTime).fromNow(withoutSuffix);
};
export const readableDifference = (from, to) => {
  const fromObject = toDateObject(from);
  const toObject = toDateObject(to);
  const differenceMS = toObject.diff(fromObject);
  if (differenceMS < 1e3) {
    return `${differenceMS} milliseconds`;
  }
  return moment.preciseDiff(fromObject, toObject);
};
export const relativeDifferenceDays = (dateTime) => {
  const eventDateObject = toDateObject(dateTime);
  const todayDateObject = toDateObject(/* @__PURE__ */ new Date());
  return todayDateObject.diff(eventDateObject, "days");
};
export const relativeDifferenceSeconds = (dateTime) => {
  const eventDateObject = toDateObject(dateTime);
  const todayDateObject = toDateObject(/* @__PURE__ */ new Date());
  return todayDateObject.diff(eventDateObject, "seconds");
};
export const isValidDate = (dateTime) => moment(dateTime, Object.values(DATE_TIME_FORMATS), true).isValid();
export const toUTCFromTz = (dateTime, sourceTimezone) => {
  if (!sourceTimezone) {
    throw new Error("It is required to define the time zone of the date time provided for internalUTCTime.");
  }
  return moment.tz(dateTime, sourceTimezone);
};
export const durationInSeconds = (duration) => moment.duration(duration).asSeconds();
export const durationInMinutes = (duration) => moment.duration(duration).asMinutes();
export const durationToMS = (duration) => moment.duration(duration).asMilliseconds();
